import React, {useCallback, useMemo} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {array, bool, number, oneOf} from "prop-types";
import {
    commentsListData,
    commentsListErrorSelector,
    commentsListHasNext,
    commentsListLoading,
    commentsListNextLoading, commentsListOrderBy, commentsListTotalItems
} from "features/components/comment-box/selectors";
import {getCommentsFetchNextPage, getCommentsStart, setCommentsOrderBy} from "features/components/comment-box/reducer";
import Text from "features/components/text";
import {__} from "services/i18n";
import {Icon} from "features/components/icon";
import Title from "features/components/title";
import Button from "features/components/button";
import {COMPANY_ROLES} from "constants.js";
import {get} from "lodash";
import Markdown from "react-remarkable";
import {deleteCommentStart} from "store/reducer/comments/delete/reducer";
import {deleteCommentErrorSelector} from "store/reducer/comments/delete/selectors";
import {withToastManager} from "react-toast-notifications";
import CommentEditModal from "features/modals/comment-edit";
import {setCommentToEdit} from "features/modals/comment-edit/reducer";
import {withTranslation} from "react-i18next";
import {voteCommentLoadingSelector} from "store/reducer/comments/vote/selectors";
import {ReducerStatuses} from "services/utils/utils";
import {voteCommentStart} from "store/reducer/comments/vote/reducer";
import Select from "features/components/select";

/* istanbul ignore next */
const mapStateToProps = state => ({
    loading: commentsListLoading(state),
    has_next_page: commentsListHasNext(state),
    next_loading: commentsListNextLoading(state),
    error: commentsListErrorSelector(state),
    data: commentsListData(state),
    total_items: commentsListTotalItems(state),
    delete_error: deleteCommentErrorSelector(state),
    comment_vote_loading: voteCommentLoadingSelector(state),
    order_by: commentsListOrderBy(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    fetchNext: getCommentsFetchNextPage,
    startFetch: getCommentsStart,
    deleteStart: deleteCommentStart,
    editComment: setCommentToEdit,
    voteComment: voteCommentStart,
    setOrderBy: setCommentsOrderBy
};

export const CommentBox = (props) => {

    const {type, loading, resource_id, next_loading, error, data, has_next_page,
        total_items, delete_error, enable_voting = false, t, comment_vote_loading,
        enable_sorting = false, sorting_options = [], order_by} = props;
    const {fetchNext, startFetch, deleteStart, editComment, voteComment, setOrderBy} = props;

    React.useEffect(() => {
        startFetch({type, id: resource_id});
    }, [resource_id, startFetch, type]);

    const nextPageCallback = useCallback(() => {
        fetchNext();
    }, [fetchNext]);

    const editCommentCallback = useCallback((comment) => {
        editComment({type, resource_id, comment})
    }, [editComment, resource_id, type]);
    const deleteCommentCallback = useCallback((comment) => {
        deleteStart({type: "video", id: resource_id, comment_id: comment.id});
    }, [deleteStart, resource_id]);

    React.useEffect(() => {
        if(!delete_error.inError) return;
        props.toastManager.add(__(error.error), { appearance: "error", id: "delete-comment-error-toast"});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delete_error]);

    const doVote = useCallback((comment) => {
        voteComment({id: resource_id, vote: !comment.votes.voted, type: type, comment_id: comment.id})
    }, [resource_id, type, voteComment]);

    const renderVoting = useCallback((comment) => {
        if (!enable_voting) return null;
        const title = `${t(comment.votes.voted ? "common.you-voted" : "")} ${comment.votes.total} ${t(comment.votes.total === 1 ? "common.votes.single" : "common.votes.plural")}`;
        return <div className={"comment-voting"}>
            <Button disabled={comment.user.me}
                    status={comment_vote_loading[comment.id] === ReducerStatuses.LOADING ? "loading" : null}
                    title={title}
                    onClick={() => doVote(comment)}
                    type={"outlined"}
                    icon={"thumbs-up"}
                    size={"small"} />
        </div>
    }, [comment_vote_loading, doVote, enable_voting, t]);

    const renderSorting = useMemo(() => {
        if (!enable_sorting) return false;
        return <Select
            value={order_by}
            onChange={setOrderBy}
            values={sorting_options}
            placeholder={__("common.order-by.placeholder")}
        />
    }, [enable_sorting, order_by, setOrderBy, sorting_options]);

    const renderAchievements = useCallback((items) => {
        if(!items) return null;
        return <div className={"achievements"}>
            {items.map(i => <div className={i.level}><Text key={i.name} size={10}>{i.name} {__(`common.achievements.level.${i.level}`)}</Text></div>)}
        </div>
    }, []);

    const renderAttachment = useCallback((attachment) => {
        if (!attachment) return null;
        const regExp = /([a-zA-Z0-9\s_\\.\-():])+(.png|.jpeg|.jpg|.gif|.bmp)$/;
        const isImage = regExp.test(attachment);
        return <div className={"comment-attachment"}>
            {isImage ?
                <img src={attachment}  alt={"comment attachment"}/> :
                <div className={"attachment-item"} onClick={() => window.open(attachment)}>
                    <Title size={16}>{__("single-thread.attachment")}</Title>
                    <Icon>eye</Icon>
                </div>
            }
        </div>
    }, []);

    const renderData = useCallback(() => {
        if(loading || error.inError) return null;
        return <div className="data">
                <div className={"top-bar"}>
                    <Title size={20}>{total_items} {__(total_items === 1 ? "common.comment.single" : "common.comment.multiple")}</Title>
                    {renderSorting}
                </div>
                {
                    data.map(comment => <div key={comment.id} className="comment-item">
                        <div className="user">
                            {comment.user.avatar ? <img src={comment.user.avatar} alt={"user avatar"} /> : <div className="profile-image"><Icon>account-circle</Icon></div>}
                            <div className="user-data">
                                <div className="name">
                                    {comment.user.speaker && <Text size={10}>{__(comment.user.speaker)}</Text>}
                                    <Title size={18}>{comment.user.me ? __("common.you") : `${comment.user.name}`}</Title>
                                    {renderAchievements(comment.achievements)}
                                </div>
                                {comment.user.me ? <>
                                    <div className="buttons">
                                        <Button title={__("buttons.edit")} onClick={() => editCommentCallback(comment)} type={"clear"} />
                                        <Text size={16}> | </Text>
                                        <Button title={__("buttons.delete")} status={"danger"} onClick={() => deleteCommentCallback(comment)} type={"clear"} />
                                    </div>
                                </> : <>
                                    <Text size={14}>{get(COMPANY_ROLES.find(i => i.key === comment.user.role), 'value', comment.user.role)}</Text>
                                    <Text size={14}>@{comment.user.company}</Text>
                                </>}
                                {renderVoting(comment)}
                            </div>
                        </div>
                        <div className="comment-text">
                            <Text size={16}><Markdown options={{linkTarget: "_blank"}} container={"span"}>{comment.comment}</Markdown></Text>
                            {renderAttachment(comment.attachment)}
                        </div>
                    </div>)
                }
                {
                    data.length === 0 && <Text size={16}>{__("common.comment.no-comments")}</Text>
                }
                {has_next_page && <div className="next-page"><Button title={__("buttons.load-more")} onClick={nextPageCallback} status={next_loading ? "loading" : null} type={"outlined"} /></div>}
            </div>
    }, [data, deleteCommentCallback, editCommentCallback, error.inError, has_next_page, loading, nextPageCallback, next_loading, renderAchievements, renderAttachment, renderSorting, renderVoting, total_items]);

    return <div className="white-box comment-box">
        {loading && <div className="centered"><Icon>loader</Icon></div>}
        {error.inError && <Text size={16} align={"center"} color={"danger"}>{__(error.error)}</Text>}
        {renderData()}
        <CommentEditModal />
    </div>
}
CommentBox.propTypes = {
    type: oneOf(["event", "video", "thread"]).isRequired,
    resource_id: number.isRequired,
    enable_voting: bool,
    enable_sorting: bool,
    sorting_options: array
};
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withToastManager(CommentBox)));